// src/components/Experience/Experience.jsx
import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";

import ADJ from "../../assets/ADJ_logo.webp";
import JBL from "../../assets/JBL_logo.webp";
import MACKIE from "../../assets/Mackie_logo2.webp";
import PRESONUS from "../../assets/Presonus_logo.webp";
import DBLOGO from "../../assets/db_logo.webp";
import CHAUVET from "../../assets/chauvet_logo.webp";

const brandLogos = [
  {
    href: "https://www.presonus.com/en/start",
    alt: "PreSonus logo",
    src: PRESONUS,
    name: "PreSonus",
  },
  {
    href: "https://www.adj.eu/",
    alt: "ADJ logo",
    src: ADJ,
    name: "ADJ",
  },
  {
    href: "https://www.chauvetprofessional.com/",
    alt: "Chauvet logo",
    src: CHAUVET,
    name: "Chauvet",
  },
  {
    href: "https://fi.jbl.com/",
    alt: "JBL logo",
    src: JBL,
    name: "JBL",
  },
  {
    href: "https://mackie.com/",
    alt: "Mackie logo",
    src: MACKIE,
    name: "Mackie",
  },
  {
    href: "https://www.dbtechnologies.com/en/home/",
    alt: "dB Technologies logo",
    src: DBLOGO,
    name: "dB Technologies",
  },
];

const trackExperienceEvent = (eventName, params = {}) => {
  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    window.gtag("event", eventName, {
      sijainti: "experience_osio",
      ...params,
    });
  }
};

const Experience = () => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            trackExperienceEvent("osio_nakyvissa", {
              osio: "experience",
            });

            // Laukeaa vain kerran
            observer.disconnect();
          }
        });
      },
      { threshold: 0.3 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="
        rounded-[2rem] border border-[#DDE7E5]
        bg-white/90 p-6 shadow-xl backdrop-blur-xl
        dark:border-darkborder dark:bg-[#111114]/95
        sm:p-8 lg:p-10
      "
      aria-labelledby="tuotemerkit-otsikko"
    >
      <div data-aos="fade-in" title="Näitä tuotemerkkejä käytämme">
        <span
          className="
            mb-4 inline-flex rounded-full border border-primary/40 bg-primary/10
            px-4 py-1.5 font-customFont1 text-xs font-extrabold uppercase
            tracking-[0.14em] !text-[#111827] shadow-sm
            dark:!text-white
          "
        >
          Laadukas kalusto
        </span>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[0.95fr_1.05fr] lg:items-end">
          <div>
            <h2
              id="tuotemerkit-otsikko"
              className="
                font-customFont2 text-4xl font-bold uppercase leading-none
                !text-[#111827]
                dark:!text-white
                sm:text-5xl lg:text-6xl
              "
            >
              Tuotemerkit
            </h2>

            <p
              className="
                mt-5 max-w-3xl font-customFont1 text-lg leading-relaxed
                !text-[#374151]
                dark:!text-[#D1D5DB]
                sm:text-xl
              "
            >
              Käytämme tapahtumissa laadukkaita ja luotettavia tuotemerkkejä,
              joiden varaan voi rakentaa toimivan äänentoiston, valotekniikan ja
              esitystekniikan. Kaluston pitää kestää käyttöä ja toimia silloin,
              kun tapahtuma on käynnissä.
            </p>
          </div>

          <div
            className="
              rounded-3xl border border-primary/30 bg-primary/10 p-5
              dark:border-primary/30 dark:bg-primary/5
            "
          >
            <p
              className="
                font-customFont1 text-base font-bold leading-relaxed
                !text-[#111827]
                dark:!text-white
              "
            >
              Valitsemme kalustoa käytännön tarpeen mukaan: selkeä ääni,
              riittävä teho, varma toiminta ja helppo käyttö ovat meille
              tärkeämpiä kuin turha monimutkaisuus.
            </p>
          </div>
        </div>

        <div
          className="
            mt-9 grid grid-cols-2 gap-4
            sm:grid-cols-3 lg:grid-cols-6
          "
        >
          {brandLogos.map((brand) => (
            <LogoItem
              key={brand.name}
              href={brand.href}
              alt={brand.alt}
              src={brand.src}
              name={brand.name}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

// Erillinen komponentti logoja varten
const LogoItem = ({ href, alt, src, name }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    title={alt}
    aria-label={`Avaa ${name} valmistajan sivu`}
    onClick={() =>
      trackExperienceEvent("tuotemerkki_klikattu", {
        tuotemerkki: name,
      })
    }
    className="
      group flex min-h-[112px] items-center justify-center rounded-2xl
      border border-[#DDE7E5] bg-[#F8FAFC] p-4 shadow-sm
      transition duration-200
      hover:-translate-y-0.5 hover:border-primary hover:bg-white hover:shadow-md
      focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary
      focus-visible:ring-offset-2 focus-visible:ring-offset-white
      dark:border-darkborder dark:bg-darksurface
      dark:hover:border-primary dark:hover:bg-[#16161A]
      dark:focus-visible:ring-offset-[#111114]
    "
  >
    <img
      src={src}
      alt={alt}
      className="
        h-12 max-w-full object-contain transition duration-200
        group-hover:scale-105
        md:h-14 xl:h-16
      "
      loading="lazy"
    />
  </a>
);

LogoItem.propTypes = {
  href: PropTypes.string.isRequired,
  alt: PropTypes.string.isRequired,
  src: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
};

export default Experience;